/**
 * Builds a list of user-visible strings from typical API / Connector error payloads.
 * Supports multiple backend shapes so one page/toast layer can render them consistently.
 *
 * Sample partner API shape:
 * `{ "errors": [ { "errorCode": "Err005", "message": "..." }, ... ] }`
 *
 * @param {object} payload - e.g. `{ message, detail, messages[], errors[] }`
 * @returns {string[]}
 */
/**
 * Backend messages sometimes pack a heading and detail onto separate lines
 * (e.g. "Existing Contact Found\nAn account with this email already exists.").
 * Flatten those onto one line so they render as a single sentence/list item.
 */
function formatMessageText(text) {
  return text
    .trim()
    .replace(/[ \t]*\r?\n[ \t\r\n]*/g, ". ")
    .replace(/\s+\./g, ".")
    .replace(/\.\s*\./g, ".")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function formatErrorsArrayEntry(entry) {
  if (typeof entry === "string" && entry.trim()) {
    return formatMessageText(entry);
  }
  if (!entry || typeof entry !== "object") {
    return null;
  }
  const msg = typeof entry.message === "string" ? entry.message.trim() : "";

  if (msg) {
    return formatMessageText(msg);
  }

  return null;
}

export function normalizeApiErrorMessages(payload) {
  if (!payload || typeof payload !== "object") {
    return [];
  }
  const msgs = [];

  // RFC 7807 / common 422 bodies often use `detail` (sometimes alongside `title`).
  if (
    payload.detail &&
    typeof payload.detail === "string" &&
    payload.detail.trim()
  ) {
    msgs.push(formatMessageText(payload.detail));
  }

  const rootMsg =
    typeof payload.message === "string" ? payload.message.trim() : "";

  if (rootMsg) {
    msgs.push(formatMessageText(rootMsg));
  }

  if (Array.isArray(payload.messages)) {
    payload.messages.forEach((m) => {
      if (typeof m === "string" && m.trim()) msgs.push(formatMessageText(m));
    });
  }

  if (Array.isArray(payload.errors)) {
    payload.errors.forEach((e) => {
      const line = formatErrorsArrayEntry(e);
      if (line) msgs.push(line);
    });
  }

  return [...new Set(msgs)];
}
